<main class="page">

  <!-- Steps -->
  <div class="steps">
    @for (step of steps(); track step.number) {
      <button type="button" class="step" [class.active]="step.active" [class.completed]="step.completed" [disabled]="!isStepClickable(step)" (click)="goToStep(step)">
        <span class="step-number">{{ step.number }}</span>
        <span class="step-label">{{ step.label }}</span>
      </button>
    }
  </div>

  <h1 class="page-title">Step 2b · Existing Project Details</h1>

  <!-- Search Section -->
  <div class="search-card">
    <p class="search-card__title">Search Existing Project</p>
    <div class="search-row">
      <div class="form-group">
        <label class="form-label">Estimate Year <span class="required">*</span></label>
        <input class="form-input search-short" type="text" [ngModel]="formData().estimateYear" (ngModelChange)="formData.update(f => ({ ...f, estimateYear: $event }))" name="estimateYear" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        @if (searchAttempted() && !formData().estimateYear.trim()) {
          <p class="field-error">Estimate Year is required.</p>
        }
      </div>
      <span class="search-dash">—</span>
      <div class="form-group">
        <label class="form-label">Sequence Number <span class="required">*</span></label>
        <input class="form-input search-short" type="text" [ngModel]="formData().sequenceNumber" (ngModelChange)="formData.update(f => ({ ...f, sequenceNumber: $event }))" name="sequenceNumber" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        @if (searchAttempted() && !formData().sequenceNumber.trim()) {
          <p class="field-error">Sequence Number is required.</p>
        }
      </div>
      <button class="btn-search" type="button" (click)="search()" [disabled]="isSubmittedStatus()">Search</button>
    </div>
    @if (searchError()) {
      <p class="search-error">{{ searchError() }}</p>
    }
    @if (searchNotFound()) {
      <p class="search-not-found">No data found for the search</p>
    }
    @if (!searchError() && !searchNotFound()) {
      <p class="search-hint">Please enter Estimate Year and Sequence Number</p>
    }
  </div>

  <!-- Form -->
  <div class="form-card">

    <!-- Section 1: Project info -->
    <div class="info-grid">

      <!-- Project Title -->
      <div class="form-group">
        <label class="form-label">
          Project Title <span class="required">*</span>
          <mat-icon class="info-icon" matTooltip="Select a project title or use Create New.">info</mat-icon>
        </label>
        @if (editingFields()['projectTitle']) {
          <input class="form-input" type="text" name="projectTitle"
            [ngModel]="formData().projectTitle"
            (ngModelChange)="updateField('projectTitle', $event)"
            [readonly]="isSubmittedStatus()"
            [disabled]="isSubmittedStatus()"
            (blur)="endEdit('projectTitle')" autofocus />
        } @else {
          <div class="edit-display-field">
            <input class="edit-display-field__input" type="text"
              [ngModel]="formData().projectTitle"
              (ngModelChange)="onAutocompleteValueChange('projectTitle', $event)"
              placeholder="— Select or type —"
              autocomplete="off"
              [matAutocomplete]="epProjectTitleOptions"
              #projectTitleTrigger="matAutocompleteTrigger"
              (focus)="openAutocomplete('projectTitle', projectTitleTrigger)"
              (click)="openAutocomplete('projectTitle', projectTitleTrigger)"
              (blur)="closeAutocomplete('projectTitle')"
              [readonly]="isSubmittedStatus()"
              [disabled]="isSubmittedStatus()" />
            <mat-autocomplete #epProjectTitleOptions="matAutocomplete">
              @for (opt of getAutocompleteOptions(projectTitleOptions, 'projectTitle', formData().projectTitle); track opt) {
                <mat-option [value]="opt">{{ opt }}</mat-option>
              }
              <mat-option value="+ Create New">+ Create New</mat-option>
            </mat-autocomplete>
            <div class="edit-overlay">
              <button type="button" class="edit-overlay__btn" (click)="startEdit('projectTitle', $event)" [disabled]="isSubmittedStatus()">Edit</button>
              <span class="edit-overlay__sep"></span>
              <span class="edit-overlay__arrow"></span>
            </div>
          </div>
        }
        @if (detailsAttempted() && !formData().projectTitle.trim()) {
          <p class="field-error">Project Title is required.</p>
        }
      </div>

      <!-- Project Number -->
      <div class="form-group">
        <label class="form-label">Project Number <span class="required">*</span></label>
        @if (editingFields()['projectNumber']) {
          <input class="form-input" type="text" name="projectNumber"
            [ngModel]="formData().projectNumber"
            (ngModelChange)="updateField('projectNumber', $event)"
            [readonly]="isSubmittedStatus()"
            [disabled]="isSubmittedStatus()"
            (blur)="endEdit('projectNumber')" autofocus />
        } @else {
          <div class="edit-display-field">
            <input class="edit-display-field__input" type="text"
              [ngModel]="formData().projectNumber"
              (ngModelChange)="onAutocompleteValueChange('projectNumber', $event)"
              placeholder="— Select or type —"
              autocomplete="off"
              [matAutocomplete]="epProjectNumberOptions"
              #projectNumberTrigger="matAutocompleteTrigger"
              (focus)="openAutocomplete('projectNumber', projectNumberTrigger)"
              (click)="openAutocomplete('projectNumber', projectNumberTrigger)"
              (blur)="closeAutocomplete('projectNumber')"
              [readonly]="isSubmittedStatus()"
              [disabled]="isSubmittedStatus()" />
            <mat-autocomplete #epProjectNumberOptions="matAutocomplete">
              @for (opt of getAutocompleteOptions(projectNumberOptions, 'projectNumber', formData().projectNumber); track opt) {
                <mat-option [value]="opt">{{ opt }}</mat-option>
              }
              <mat-option value="+ Create New">+ Create New</mat-option>
            </mat-autocomplete>
            <div class="edit-overlay">
              <button type="button" class="edit-overlay__btn" (click)="startEdit('projectNumber', $event)" [disabled]="isSubmittedStatus()">Edit</button>
              <span class="edit-overlay__sep"></span>
              <span class="edit-overlay__arrow"></span>
            </div>
          </div>
        }
        @if (detailsAttempted() && !formData().projectNumber.trim()) {
          <p class="field-error">Project Number is required.</p>
        }
      </div>

      <!-- Estimate Title -->
      <div class="form-group">
        <label class="form-label">
          Estimate Title <span class="required">*</span>
          <mat-icon class="info-icon" matTooltip="Select an estimate title or use Create New.">info</mat-icon>
        </label>
        @if (editingFields()['estimateTitle']) {
          <input class="form-input" type="text" name="estimateTitle"
            [ngModel]="formData().estimateTitle"
            (ngModelChange)="updateField('estimateTitle', $event)"
            [readonly]="isSubmittedStatus()"
            [disabled]="isSubmittedStatus()"
            (blur)="endEdit('estimateTitle')" autofocus />
        } @else {
          <div class="edit-display-field">
            <input class="edit-display-field__input" type="text"
              [ngModel]="formData().estimateTitle"
              (ngModelChange)="onAutocompleteValueChange('estimateTitle', $event)"
              placeholder="— Select or type —"
              autocomplete="off"
              [matAutocomplete]="epEstimateTitleOptions"
              #estimateTitleTrigger="matAutocompleteTrigger"
              (focus)="openAutocomplete('estimateTitle', estimateTitleTrigger)"
              (click)="openAutocomplete('estimateTitle', estimateTitleTrigger)"
              (blur)="closeAutocomplete('estimateTitle')"
              [readonly]="isSubmittedStatus()"
              [disabled]="isSubmittedStatus()" />
            <mat-autocomplete #epEstimateTitleOptions="matAutocomplete">
              @for (opt of getAutocompleteOptions(estimateTitleOptions, 'estimateTitle', formData().estimateTitle); track opt) {
                <mat-option [value]="opt">{{ opt }}</mat-option>
              }
              <mat-option value="+ Create New">+ Create New</mat-option>
            </mat-autocomplete>
            <div class="edit-overlay">
              <button type="button" class="edit-overlay__btn" (click)="startEdit('estimateTitle', $event)" [disabled]="isSubmittedStatus()">Edit</button>
              <span class="edit-overlay__sep"></span>
              <span class="edit-overlay__arrow"></span>
            </div>
          </div>
        }
        @if (detailsAttempted() && !formData().estimateTitle.trim()) {
          <p class="field-error">Estimate Title is required.</p>
        }
      </div>

      <!-- Project Code -->
      <div class="form-group">
        <label class="form-label">Project Code</label>
        @if (editingFields()['projectCode']) {
          <input class="form-input" type="text" name="projectCode"
            [ngModel]="formData().projectCode"
            (ngModelChange)="updateField('projectCode', $event)"
            [readonly]="isSubmittedStatus()"
            [disabled]="isSubmittedStatus()"
            (blur)="endEdit('projectCode')" autofocus />
        } @else {
          <div class="edit-display-field">
            <input class="edit-display-field__input" type="text"
              [ngModel]="formData().projectCode"
              (ngModelChange)="onAutocompleteValueChange('projectCode', $event)"
              placeholder="— Select or type —"
              autocomplete="off"
              [matAutocomplete]="epProjectCodeOptions"
              #projectCodeTrigger="matAutocompleteTrigger"
              (focus)="openAutocomplete('projectCode', projectCodeTrigger)"
              (click)="openAutocomplete('projectCode', projectCodeTrigger)"
              (blur)="closeAutocomplete('projectCode')"
              [readonly]="isSubmittedStatus()"
              [disabled]="isSubmittedStatus()" />
            <mat-autocomplete #epProjectCodeOptions="matAutocomplete">
              @for (opt of getAutocompleteOptions(projectCodeOptions, 'projectCode', formData().projectCode); track opt) {
                <mat-option [value]="opt">{{ opt }}</mat-option>
              }
              <mat-option value="+ Create New">+ Create New</mat-option>
            </mat-autocomplete>
            <div class="edit-overlay">
              <button type="button" class="edit-overlay__btn" (click)="startEdit('projectCode', $event)" [disabled]="isSubmittedStatus()">Edit</button>
              <span class="edit-overlay__sep"></span>
              <span class="edit-overlay__arrow"></span>
            </div>
          </div>
        }
      </div>

      <div class="form-group"></div>
    </div>

    <hr class="divider" />

    <!-- Section 2: Details -->
    <div class="form-grid">
      <div class="form-group">
        <label class="form-label">Estimate Request Type <span class="required">*</span></label>
        <input class="form-select" [ngModel]="formData().estimateRequestType" (ngModelChange)="onSelectValueChange('estimateRequestType', $event)" name="estimateRequestType" placeholder="— Select or type —" autocomplete="off" [matAutocomplete]="epEstimateRequestTypeOptions" #estimateRequestTypeTrigger="matAutocompleteTrigger" (focus)="openAutocomplete('estimateRequestType', estimateRequestTypeTrigger)" (click)="openAutocomplete('estimateRequestType', estimateRequestTypeTrigger)" (blur)="closeAutocomplete('estimateRequestType')" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        <mat-autocomplete #epEstimateRequestTypeOptions="matAutocomplete">
          @for (opt of getAutocompleteOptions(estimateRequestTypes, 'estimateRequestType', formData().estimateRequestType); track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
        @if (detailsAttempted() && !formData().estimateRequestType) {
          <p class="field-error">Estimate Request Type is required.</p>
        }
      </div>
      <div class="form-group">
        <label class="form-label">Project Management Type <span class="required">*</span></label>
        <input class="form-select" [ngModel]="formData().projectManagementType" (ngModelChange)="onProjectManagementTypeChange($event)" name="projectManagementType" placeholder="— Select or type —" autocomplete="off" [matAutocomplete]="epProjectManagementTypeOptions" #projectManagementTypeTrigger="matAutocompleteTrigger" (focus)="openAutocomplete('projectManagementType', projectManagementTypeTrigger)" (click)="openAutocomplete('projectManagementType', projectManagementTypeTrigger)" (blur)="closeAutocomplete('projectManagementType')" [readonly]="isProjectManagementTypeReadonly()" [disabled]="isProjectManagementTypeReadonly()" />
        <mat-autocomplete #epProjectManagementTypeOptions="matAutocomplete">
          @for (opt of getAutocompleteOptions(projectManagementTypes, 'projectManagementType', formData().projectManagementType); track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
        @if (detailsAttempted() && !formData().projectManagementType) {
          <p class="field-error">Project Management Type is required.</p>
        }
      </div>
      <div class="form-group">
        <label class="form-label">Unit <span class="required">*</span></label>
        <input class="form-select" [ngModel]="formData().unit" (ngModelChange)="onSelectValueChange('unit', $event)" name="unit" placeholder="— Select or type —" autocomplete="off" [matAutocomplete]="epUnitOptions" #unitTrigger="matAutocompleteTrigger" (focus)="openAutocomplete('unit', unitTrigger)" (click)="openAutocomplete('unit', unitTrigger)" (blur)="closeAutocomplete('unit')" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        <mat-autocomplete #epUnitOptions="matAutocomplete">
          @for (opt of getAutocompleteOptions(unitOptions, 'unit', formData().unit); track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
        @if (detailsAttempted() && !formData().unit) {
          <p class="field-error">Unit is required.</p>
        }
      </div>

      <div class="form-group">
        <label class="form-label">Planned Project Completion Date <span class="required">*</span></label>
        <input class="form-input" type="date" [ngModel]="formData().anticipatedCompletionDate" (ngModelChange)="formData.update(f => ({ ...f, anticipatedCompletionDate: $event }))" name="anticipatedCompletionDate" (click)="openDatePicker($event)" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        @if (detailsAttempted() && !formData().anticipatedCompletionDate) {
          <p class="field-error">Planned Project Completion Date is required.</p>
        }
      </div>
      <div class="form-group">
        <label class="form-label">CPDEP Phase <span class="required">*</span></label>
        <input class="form-select readonly-field" [ngModel]="formData().cpdepPhase" name="cpdepPhase" placeholder="— Auto-filled —" autocomplete="off" readonly />
        @if (detailsAttempted() && !formData().cpdepPhase) {
          <p class="field-error">CPDEP Phase is required.</p>
        }
      </div>
      <div class="form-group">
        <label class="form-label">Area <span class="required">*</span></label>
        <input class="form-select" [ngModel]="formData().area" (ngModelChange)="onSelectValueChange('area', $event)" name="area" placeholder="— Select or type —" autocomplete="off" [matAutocomplete]="epAreaOptions" #areaTrigger="matAutocompleteTrigger" (focus)="openAutocomplete('area', areaTrigger)" (click)="openAutocomplete('area', areaTrigger)" (blur)="closeAutocomplete('area')" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        <mat-autocomplete #epAreaOptions="matAutocomplete">
          @for (opt of getAutocompleteOptions(areaOptions, 'area', formData().area); track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
        @if (detailsAttempted() && !formData().area) {
          <p class="field-error">Area is required.</p>
        }
      </div>

      <div class="form-group">
        <label class="form-label">Preliminary Cost Estimate Deadline <span class="required">*</span></label>
        <input class="form-input" type="date" [ngModel]="formData().preliminaryCostDeadline" (ngModelChange)="formData.update(f => ({ ...f, preliminaryCostDeadline: $event }))" name="preliminaryCostDeadline" (click)="openDatePicker($event)" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        @if (detailsAttempted() && !formData().preliminaryCostDeadline) {
          <p class="field-error">Preliminary Cost Estimate Deadline is required.</p>
        }
      </div>
      <div class="form-group">
        <label class="form-label">Design progress <span class="required">*</span></label>
        <input class="form-select readonly-field" [ngModel]="formData().designProgress" name="designProgress" placeholder="— Auto-filled —" autocomplete="off" readonly />
        @if (detailsAttempted() && !formData().designProgress) {
          <p class="field-error">Design progress is required.</p>
        }
      </div>
      <div class="form-group">
        <label class="form-label">IBP Year<span class="required">*</span></label>
        <input class="form-select" [ngModel]="formData().ibp" (ngModelChange)="onSelectValueChange('ibp', $event)" name="ibp" placeholder="— Select or type —" autocomplete="off" [matAutocomplete]="epIbpOptions" #ibpTrigger="matAutocompleteTrigger" (focus)="openAutocomplete('ibp', ibpTrigger)" (click)="openAutocomplete('ibp', ibpTrigger)" (blur)="closeAutocomplete('ibp')" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        <mat-autocomplete #epIbpOptions="matAutocomplete">
          @for (opt of getAutocompleteOptions(ibpOptions, 'ibp', formData().ibp); track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
        @if (detailsAttempted() && !formData().ibp) {
          <p class="field-error">IBP Year is required.</p>
        }
      </div>

      <div class="form-group"></div>
      <div class="form-group"></div>
      <div class="form-group">
        <label class="form-label">TA Year <span class="required">*</span></label>
        <input class="form-select" [ngModel]="formData().taYear" (ngModelChange)="onSelectValueChange('taYear', $event)" name="taYear" placeholder="— Select or type —" autocomplete="off" [matAutocomplete]="epTaYearOptions" #taYearTrigger="matAutocompleteTrigger" (focus)="openAutocomplete('taYear', taYearTrigger)" (click)="openAutocomplete('taYear', taYearTrigger)" (blur)="closeAutocomplete('taYear')" [readonly]="isSubmittedStatus()" [disabled]="isSubmittedStatus()" />
        <mat-autocomplete #epTaYearOptions="matAutocomplete">
          @for (opt of getAutocompleteOptions(taYearOptions, 'taYear', formData().taYear); track opt) {
            <mat-option [value]="opt">{{ opt }}</mat-option>
          }
        </mat-autocomplete>
        @if (detailsAttempted() && !formData().taYear) {
          <p class="field-error">TA Year is required.</p>
        }
      </div>
    </div>

  </div>

  <!-- Actions -->
  <div class="actions">
    <button class="btn btn-secondary" type="button" (click)="goBack()">‹ Back to Step 1</button>
    <div class="actions-right">
      @if (shouldShowSaveDraft()) {
        <button class="btn btn-outline" type="button" (click)="saveDraft()" [disabled]="isSubmittedStatus()">Save Draft</button>
      }
      @if (shouldShowSave()) {
        <button class="btn btn-outline" type="button" (click)="save()" [disabled]="isSubmittedStatus()">Save</button>
      }
      <button class="btn btn-primary" type="button" (click)="goToQuestionnaire()">Go To Questionnaire ›</button>
    </div>
  </div>

  @if (showPmtConfirmModal()) {
    <div class="modal-backdrop" (click)="cancelPmtChange()"></div>
    <div class="confirm-modal">
      <p class="confirm-modal__title">Change Project Management Type</p>
      <p class="confirm-modal__message">
        You are about to change Project Management Type. If you click &ldquo;YES&rdquo; all your answers in the Questionnaire (step 3) will be removed. Please click checkbox to auto-download your answers. &ldquo;CANCEL&rdquo; if you don&rsquo;t wish to continue.
      </p>
      <label class="confirm-modal__checkbox">
        <input
          type="checkbox"
          [ngModel]="autoDownloadAnswers()"
          (ngModelChange)="autoDownloadAnswers.set($event)"
        />
        <span>Auto-download questionnaire answers</span>
      </label>
      <div class="confirm-modal__actions">
        <button type="button" class="btn-confirm btn-confirm--cancel" (click)="cancelPmtChange()">Cancel</button>
        <button type="button" class="btn-confirm btn-confirm--confirm" [disabled]="isDownloadingAnswers()" (click)="confirmPmtChange()">
          {{ isDownloadingAnswers() ? 'Downloading...' : 'Yes' }}
        </button>
      </div>
    </div>
  }

</main>
